import { useState, useEffect, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import styled from '@emotion/styled'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import InfiniteScroll from 'react-infinite-scroll-component'
import LazyLoad from 'react-lazyload'
import { flatten, floor, isNumber } from 'lodash-es'
import {
  useArticleClassifications,
  useInfiniteArticles,
  useBanners,
} from 'utils/api/hooks'
import { selectWebview } from 'store/webview/webviewSlice'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import Footer from 'components/common/Footer'
import ContentWrapper from 'components/common/ContentWrapper'
import Link from 'components/common/A'
import ArticleListItem from 'components/common/ArticleListItem'
import InfiniteContainer from 'components/common/InfiniteContainer'
import NowrapDiv from 'components/common/NowrapDiv'
import { Tabs, Tab } from 'components/common/Present/Tabs'
import LatestArticle from 'components/common/LatestArticle'
import GuessLikesList from 'components/common/GuessLikesList'
import { Solid } from 'components/common/Divider'
import { HideInDesktopBox, HideInMobileBox } from 'components/common/Hidden'
import { AdvertisementBanner } from 'components/common/Banner'

const a11yProps = (index) => {
  return {
    id: `scrollable-tab-${index}`,
    'aria-controls': `scrollable-tab-panel-${index}`,
  }
}

const CustomLink = styled(Link)`
  ${({ theme }) => `
    transition-delay: 0s;
    transition-duration: .2s;
    transition-property: color;
    transition-timing-function: ease;

    &:hover {
      color: ${theme.palette.primary.main};
    }
  `}
`

const ClassificationOfArticle = ({ fallbackData }) => {
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const webview = useSelector(selectWebview)

  const [tabIndex, setTabIndex] = useState(fallbackData?.tabIndex || -1)
  useEffect(() => {
    if (isNumber(fallbackData?.tabIndex)) setTabIndex(fallbackData.tabIndex)
  }, [fallbackData?.tabIndex])

  const { data: articleClassifications } = useArticleClassifications(null, {
    fallbackData: fallbackData?.articleClassifications,
  })
  const visibleArticleClassifications = articleClassifications.filter(
    (item) => !!item.isVisible
  )
  const { data, size, setSize } = useInfiniteArticles(
    () => ({
      articleClassificationIds: [visibleArticleClassifications[tabIndex]._id],
    }),
    { fallbackData: [fallbackData?.articles] }
  )
  const articles = flatten(data)

  const { data: advertisementBanner } = useBanners(webview?.name, {
    displayAt: 'articleListAdvertisement',
  })
  const bannerList = flatten(advertisementBanner)

  const onLoadMore = () => {
    setSize(size + 1)
  }

  const router = useRouter()
  const onTabChange = (event, newTabIndex) => {
    setTabIndex(newTabIndex)
    const classification = visibleArticleClassifications?.[newTabIndex]
    router.push(`/article-classification/${classification.urlId}`)
  }

  useEffect(() => {
    const routeChangeCompleteHandler = (url) => {
      const path = url.split('?')[0]
      const urlIdFromUrl = path.split('/article-classification/')[1]
      const articleClassification = visibleArticleClassifications?.[tabIndex]
      if (articleClassification?.urlId !== urlIdFromUrl) {
        const index = visibleArticleClassifications?.findIndex(
          (classification) => classification.urlId === urlIdFromUrl
        )
        if (index > -1) {
          setTabIndex(index)
        }
      }
    }
    if (router.isReady) {
      router.events.on('routeChangeComplete', routeChangeCompleteHandler)
    }
    return () => {
      router.events.off('routeChangeComplete', routeChangeCompleteHandler)
    }
  }, [router.isReady, tabIndex])

  const containerId = 'scrollable-article-classification-container'
  useEffect(() => {
    if (webview?.name === 'lsApp') {
      try {
        enableNativeHeaderMainTabs(false)
        enableNativeHeaderNavBack(true)
        enableNativeHeaderSearch(true)
      } catch (err) {
        console.error('native notifier error: ', err.message)
      }
    }
  }, [webview])

  return (
    <Layout stickyHeader={<Header />} fixContentHeight>
      <InfiniteContainer id={containerId}>
        {webview?.name !== 'lsApp' && isLarge && (
          <ContentWrapper mt={4}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {[
                <CustomLink href="/" passHref key="1" $origin>
                  <Typography variant="body2">
                    <NowrapDiv>首頁</NowrapDiv>
                  </Typography>
                </CustomLink>,
                <CustomLink href="/article" passHref key="2" $origin>
                  <Typography variant="body2">
                    <NowrapDiv>找好文</NowrapDiv>
                  </Typography>
                </CustomLink>,
                <Typography variant="body2" color="textSecondary">
                  <NowrapDiv key="3">
                    {visibleArticleClassifications?.[tabIndex]?.name}
                  </NowrapDiv>
                </Typography>,
              ]}
            </Breadcrumbs>
          </ContentWrapper>
        )}

        <ContentWrapper mt={{ xs: 8, lg: 12 }}>
          <Tabs
            value={tabIndex}
            onChange={onTabChange}
            variant="scrollable"
            indicatorColor="primary"
            textColor="primary"
            scrollButtons
            aria-label="article classification tabs"
          >
            {visibleArticleClassifications?.map((classification, index) => (
              <Tab
                key={classification._id}
                label={classification.name}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </ContentWrapper>
        <ContentWrapper mt={{ xs: 6, lg: 8 }} mb={6}>
          <Grid container>
            <Grid
              item
              xs={12}
              lg={8}
              pr={{ xxl: 12, maxLimit: 24 }}
              px={{ md: 8, lg: 0 }}
            >
              <InfiniteScroll
                dataLength={articles?.length || 0}
                hasMore={true}
                next={onLoadMore}
                scrollableTarget={containerId}
                scrollThreshold={0.6}
              >
                {articles?.map((article, index) => (
                  <Fragment key={article._id}>
                    <LazyLoad
                      height={350}
                      offset={350}
                      scrollContainer={`#${containerId}`}
                    >
                      <Box key={article._id}>
                        <Box mb={{ xs: 8, md: 15 }}>
                          <ArticleListItem article={article} />
                        </Box>
                        {/* 每隔十則文章顯示一個banner */}
                        {(index + 1) % 10 === 0 &&
                        bannerList?.[floor((index + 1) / 10) - 1] ? (
                          <AdvertisementBanner
                            banner={bannerList?.[floor((index + 1) / 10) - 1]}
                            bannerLocation="文章列表頁"
                          />
                        ) : (
                          ''
                        )}
                        {/* 不足十則也顯示一個banner */}
                        {index === articles?.length - 1 &&
                        (index + 1) % 10 !== 0 &&
                        bannerList?.[floor((index + 1) / 10)] ? (
                          <AdvertisementBanner
                            banner={bannerList?.[floor((index + 1) / 10)]}
                            bannerLocation="文章列表頁"
                          />
                        ) : (
                          ''
                        )}
                      </Box>
                    </LazyLoad>
                    <noscript>
                      <style>{`.lazyload-placeholder { display: none; }`}</style>
                      <Box key={article._id}>
                        <Box mb={{ xs: 8, md: 15 }}>
                          <ArticleListItem article={article} />
                        </Box>
                        {/* 每隔十則文章顯示一個banner */}
                        {(index + 1) % 10 === 0 &&
                        bannerList?.[floor((index + 1) / 10) - 1] ? (
                          <AdvertisementBanner
                            banner={bannerList?.[floor((index + 1) / 10) - 1]}
                            bannerLocation="文章列表頁"
                          />
                        ) : (
                          ''
                        )}
                        {/* 不足十則也顯示一個banner */}
                        {index === articles?.length - 1 &&
                        (index + 1) % 10 !== 0 &&
                        bannerList?.[floor((index + 1) / 10)] ? (
                          <AdvertisementBanner
                            banner={bannerList?.[floor((index + 1) / 10)]}
                            bannerLocation="文章列表頁"
                          />
                        ) : (
                          ''
                        )}
                      </Box>
                    </noscript>
                  </Fragment>
                ))}
                <Solid mt={{ xs: 18, xl: 24 }} />
              </InfiniteScroll>
              <HideInMobileBox>
                <LatestArticle mt={12} />
              </HideInMobileBox>
            </Grid>
            <Grid
              item
              xs={12}
              lg={4}
              px={{ md: 8, lg: 0 }}
              pl={{ lg: 20, xl: 24, xxl: 38, maxLimit: 41 }}
            >
              <GuessLikesList
                articleClassification={visibleArticleClassifications[tabIndex]}
                mt={{ xs: 12, lg: 0 }}
              />
              <HideInDesktopBox>
                <LatestArticle mt={12} />
              </HideInDesktopBox>
            </Grid>
          </Grid>
        </ContentWrapper>

        {webview?.name !== 'lsApp' && <Footer />}
      </InfiniteContainer>
    </Layout>
  )
}

export default ClassificationOfArticle
